import api from "@/assets/js/utils/api.js";
import Swal from "sweetalert2";
import {
  showConfirm,
  showSuccess,
  showError,
  showToast,
  showHtmlPopup,
  showLoading,
  closePopup,
} from "@/assets/js/utils/alertService";

export default {
  name: "CartPage",
  data() {
    return {
      cartData: null, // 後端購物車資料
      cartItems: [], // 購物車商品列表
      shippingFee: 0,
      shippingProductId: 1,
      shippingPrice: 165,
      // 優惠券資料（保留模擬資料或改為從後端取得）
      coupons: [],
      selectedCoupon: null,
    };
  },

  computed: {
    totalAmount() {
      // 使用後端資料的 TotalAmount
      return this.cartData ? this.cartData.TotalAmount : 0;
    },

    shippingFeeAuto() {
      return this.totalAmount >= 3000 ? 0 : this.shippingPrice;
    },

    finalAmount() {
      // 後端已計算 FinalAmount
      return this.cartData ? this.cartData.FinalAmount : 0;
    },

    discountAmount() {
      // 後端已計算 DiscountAmount
      return this.cartData ? this.cartData.DiscountAmount : 0;
    },

    // 購物車總件數
    totalItems() {
      return this.cartData ? this.cartData.ItemCount : 0;
    },

    subtotalWithoutShipping() {
      return this.cartItems
        .filter((i) => i.productId !== this.shippingProductId) // 排除運費商品
        .reduce((sum, i) => sum + i.subtotal, 0); // 累加小計
    },
  },

  async mounted() {
    // 載入購物車資料
    await this.loadCart();

    // 暴露至全域讓 Swal 的 button 能呼叫
    window.applyCoupon = (id) => {
      this.applyCoupon(id);
      closePopup();
    };

    window.confirmApplyCoupon = (id) => {
      this.confirmApplyCoupon(id); // 先跑警告，再決定是否真的套用
    };
  },

  methods: {
    // 載入購物車資料
    async loadCart() {
      try {
        const res = await api.get("/front/cart");

        if (res.data?.success) {
          this.cartData = res.data.data;

          // ✅ 更新商品列表
          this.cartItems = (res.data.data.Details || []).map((item) => ({
            id: item.Id,
            productId: item.ProductId,
            name: item.ProductName,
            variantId: item.VariantId,
            variantName: item.VariantName || null,
            price: item.UnitPrice,
            quantity: item.Quantity,
            subtotal: item.Subtotal,
            image: item.PictureURL || require("@/assets/image/AYH.webp"),
          }));

          // ✅ 如果購物車有優惠券就顯示
          if (this.cartData.CouponId) {
            this.selectedCoupon = {
              id: this.cartData.CouponId,
              name: this.cartData.CouponName || "已套用優惠券",
              discount: this.cartData.DiscountAmount || 0, // 折扣金額
            };
          } else {
            this.selectedCoupon = null;
          }

          await this.syncShippingItem();
        } else {
          this.cartItems = [];
          this.cartData = null;
          this.selectedCoupon = null;
        }
      } catch (error) {
        console.error("載入購物車失敗:", error);
        if (error.response?.status === 401) {
          showError("請先登入");
          this.$router.push("/login");
        } else {
          showError("載入購物車失敗，請稍後再試");
        }
      }
    },
    // 同步運費商品
    async syncShippingItem() {
      const hasShipping = this.cartItems.some(
        (i) => i.productId === this.shippingProductId
      );

      // 判斷「真商品金額」
      if (this.subtotalWithoutShipping < 3000 && !hasShipping) {
        await api.post("/front/cart/add", {
          ProductId: this.shippingProductId,
          Qty: 1,
        });
        await this.loadCart();
      } else if (this.subtotalWithoutShipping >= 3000 && hasShipping) {
        await api.post("/front/cart/add", {
          ProductId: this.shippingProductId,
          Qty: 0,
        });
        await this.loadCart();
      }
    },
    // 更新商品數量
    async updateItemQuantity(item, newQuantity) {
      try {
        if (newQuantity < 0) {
          showError("數量不能小於 0");
          return;
        }

        // 🔥 記錄修改前的小計（用於判斷是否跨越 3000）
        const beforeAmount = this.totalAmount;
        const res = await api.post("/front/cart/add", {
          ProductId: item.productId,
          Qty: newQuantity,
        });

        if (res.data?.success) {
          // 重新載入購物車
          await this.loadCart();
          // 🔥 判斷是否從免運 → 要收運費
          if (beforeAmount >= 3000 && this.totalAmount < 3000) {
            Swal.fire({
              icon: "warning",
              title: "提醒您",
              html: `
            您的訂單未滿 <b>NT$ 3,000</b><br>
            將酌收 <b style="color:red;">NT$ 165</b> 運費
          `,
              confirmButtonText: "了解",
              confirmButtonColor: "#d33",
            });
          }

          // 🔥 判斷是否從要收運費 → 免運
          if (beforeAmount < 3000 && this.totalAmount >= 3000) {
            Swal.fire({
              icon: "success",
              title: "已達免運門檻",
              html: `
            🎉 恭喜！您的訂單金額已滿 <b>NT$ 3,000</b><br>
            可享有 <b style="color:green;">免運</b> 優惠！
          `,
              confirmButtonText: "太好了",
              confirmButtonColor: "#28a745",
            });
          }

          if (newQuantity === 0) {
            showSuccess("商品已從購物車移除");
          }
          // ✅ 移除數量更新的提示訊息
        } else {
          showError(res.data?.message || "更新失敗");
        }
      } catch (error) {
        console.error("更新數量失敗:", error);
        showError("更新失敗，請稍後再試");
      }
    },

    formatPrice(price) {
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    async increaseQuantity(item) {
      if (item.price === 0) {
        showError("贈品數量不可更改");
        return;
      }
      if (item.quantity < 99) {
        await this.updateItemQuantity(item, item.quantity + 1);
      } else {
        showError("最多 99 件");
      }
    },

    async decreaseQuantity(item) {
      if (item.price === 0) {
        showError("贈品數量不可更改");
        return;
      }
      if (item.quantity > 1) {
        await this.updateItemQuantity(item, item.quantity - 1);
      } else {
        // 如果數量為1，減少就是刪除
        await this.removeItem(item);
      }
    },

    async updateQuantity(item, inputQuantity) {
      if (item.price === 0) {
        showError("贈品數量不可更改");
        return;
      }
      const quantity = parseInt(inputQuantity);

      if (isNaN(quantity) || quantity < 0) {
        showError("請輸入有效數量");
        return;
      }

      if (quantity > 99) {
        showError("最多 99 件");
        return;
      }

      await this.updateItemQuantity(item, quantity);
    },

    // 刪除商品
    async removeItem(item) {
      if (item.price === 0) {
        showError("贈品無法刪除");
        return;
      }
      const result = await showConfirm(`確定要刪除「${item.name}」嗎？`);

      if (result.isConfirmed) {
        await this.updateItemQuantity(item, 0);
      }
    },

    // 前往結帳
    goToCheckout() {
      if (this.cartItems.length === 0) {
        showError("購物車是空的");
        return;
      }

      showToast("前往結帳");
      this.$router.push("/checkout");
    },

    // 前往產品頁
    goToProducts() {
      this.$router.push("/products");
    },

    // 清空購物車
    async clearCart() {
      if (this.cartItems.length === 0) {
        showToast("購物車已經是空的");
        return;
      }

      const result = await showConfirm("你確定要清空購物車嗎？");

      if (result.isConfirmed) {
        try {
          showLoading("清空購物車中...");

          // 逐一刪除所有商品
          for (const item of this.cartItems) {
            await api.post("/front/cart/add", {
              ProductId: item.productId,
              Qty: 0,
            });
          }

          await this.loadCart();
          showSuccess("購物車已清空");
        } catch (error) {
          console.error("清空購物車失敗:", error);
          showError("清空失敗，請稍後再試");
        }
      }
    },

    // 優惠券相關方法保持不變
    async openCouponDialog() {
      try {
        // ✅ 向後端取得用戶可用優惠券
        const res = await api.get("/front/coupon");

        if (!res.data?.success) {
          showError("載入優惠券失敗");
          return;
        }

        // 🔹 原始優惠券資料
        const coupons = res.data.data || [];

        // ✅ 過濾條件：只保留「可使用」而且未過期
        const now = new Date();
        const availableCoupons = coupons.filter(
          (c) =>
            c.Status === "可使用" &&
            new Date(c.EndDate) >= now && // 未過期
            !c.IsUsed // 未使用
        );

        if (availableCoupons.length === 0) {
          showToast("目前沒有可用的優惠券");
          return;
        }

        // ✅ 將後端資料轉換成 HTML 顯示格式
        let html = availableCoupons
          .map((c) => {
            const start = new Date(c.StartDate).toLocaleDateString();
            const end = new Date(c.EndDate).toLocaleDateString();
            const desc =
              c.Description && c.Description.trim() !== ""
                ? `<p style="margin:4px 0 0 0; font-size:13px; color:#666;">${c.Description}</p>`
                : "";

            return `
          <div style="margin-bottom:10px; padding:10px; border:1px solid #ddd; border-radius:8px; background:#f9f9f9;">
            <b>${c.CouponName}</b>
            <br>
            <small>有效期限：${start} ~ ${end}</small>
            ${desc}
            <br>
           <button 
  style="margin-top:8px; padding:6px 10px; border:none; border-radius:6px; background:#3b3b7e; color:white; cursor:pointer;"
  onclick="window.confirmApplyCoupon(${c.CouponId})">
  套用
</button>

          </div>
        `;
          })
          .join("");

        await showHtmlPopup("可用優惠券", html, 450);
      } catch (err) {
        console.error("載入優惠券失敗:", err);

        showError("載入優惠券失敗，請稍後再試");
      }
    },

    // 套用優惠券（呼叫後端）
    async applyCoupon(id) {
      try {
        showLoading("優惠券套用中..."); // 顯示 loading

        const res = await api.post("/front/cart/apply-coupon", {
          CouponId: id, // 傳給後端的優惠券 ID
        });

        closePopup(); // 關閉優惠券視窗

        if (res.data?.success) {
          showSuccess("優惠券已成功套用");

          // 套用成功後重新載入購物車（後端會計算折扣）
          await this.loadCart();
        } else {
          showError(res.data?.message || "優惠券套用失敗");
        }
      } catch (error) {
        console.error("套用優惠券失敗:", error);
        showError("優惠券套用失敗，請稍後再試");
      }
    },
    cancelCoupon() {
      this.selectedCoupon = null;
      showToast("優惠券已取消");
    },

    // 套用前警告
    async confirmApplyCoupon(id) {
      const result = await Swal.fire({
        icon: "warning",
        title: "請確認",
        html: `
      確定要套用這張優惠券嗎？<br>
      <b style="color:red;">套用後將無法取消！</b>
    `,
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "確定",
        cancelButtonText: "取消",
      });

      if (result.isConfirmed) {
        await this.applyCoupon(id);
      }
    },
  },
};
